import { Fragment } from "react";
import { setReportsData } from "../../../store/reportsSlice";
import Table from "../../../components/table/table";
import { useAppSelector } from "../../../hooks";
import {
  CheckRoleBasedPermission,
  permission,
  services,
} from "../../../utility/checkRoleBasedPermission";
import { Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Report } from "../../../types";
import Classes from "./reportServiceNew.module.css";
import { formatDuration, intervalToDuration } from "date-fns";

const ReportServiceNew = () => {
  const reportsData = useAppSelector((state) => state?.reportsData);
  const auth = useAppSelector((state) => state?.auth);
  const navigate = useNavigate();

  const DurationRenderCell = (row: Report) => {
    const duration = intervalToDuration({
      start: 0,
      end: (row?.duration || 0) * 1000,
    });
    const formattedDuration = formatDuration(duration, {
      format: ["hours", "minutes", "seconds"],
    });

    return (
      <p className={Classes.DurationContent}>{formattedDuration || "0s"}</p>
    );
  };

  const TotalRenderCell = (row: Report) => {
    return (
      <p
        className={Classes.Total}
        style={{
          background: "#627d98",
          color: "#fff",
          fontWeight: 600,
          height: "fit-content",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {row?.total ? row?.total : 0}
      </p>
    );
  };

  const SuccessRenderCell = (row: Report) => {
    return (
      <p
        className={Classes.Success}
        style={{
          background: "#0A9154",
          color: "#fff",
          fontWeight: 600,
          height: "fit-content",
        }}
      >
        {row?.success ? row?.success : 0}
      </p>
    );
  };

  const FailedRenderCell = (row: Report) => {
    return (
      <p
        className={Classes.Failed}
        style={{
          background: "#e12d39",
          color: "#fff",
          fontWeight: 600,
          height: "fit-content",
        }}
      >
        {row?.failed ? row?.failed : 0}
      </p>
    );
  };

  const CreatedByRenderCell = (row: Report) => {
    return (
      <Chip
        label={`${row?.created_by_name}`}
        component="a"
        onClick={(event) => {
          event.stopPropagation();
          CheckRoleBasedPermission(
            auth?.user,
            services.users,
            permission.read
          ) && navigate(`/users/${row?.created_by}`);
        }}
        clickable
      />
    );
  };

  const createdFrom = ["Development", "Localhost", "Production"];
  const columnVisibilityModel = { created_at: false };
  type params = { row: Report };
  let columns = [
    {
      field: "id",
      headerName: "Report Id",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: true,
      type: "number",
      sortable: false,
    },
    {
      field: "service_name",
      headerName: "Report Name",
      flex: 2,
      headerClassName: "super-app-theme--header",
      type: "enum",
      tableName: "report",
      columnName: "service_name",
      sortable: false,
    },
    {
      field: "updated_at",
      headerName: "Last Updated At",
      flex: 1.2,
      headerClassName: "super-app-theme--header",
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Started At",
      flex: 1,
      headerClassName: "super-app-theme--header",
      sortable: false,
    },
    {
      field: "created_by_name",
      headerName: "Created By",
      flex: 1.5,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      renderCell: (params: params) => <CreatedByRenderCell {...params?.row} />,
      customRenderCell: CreatedByRenderCell,
      type: "enum",
      tableName: "admins",
      columnName: "name",
      sortable: false,
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: true,
      renderCell: (params: params) => <DurationRenderCell {...params?.row} />,
      customRenderCell: DurationRenderCell,
      type: "number",
      sortable: false,
    },
    {
      field: "env",
      headerName: "Created From",
      flex: 1.2,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      enumValues: createdFrom,
      type: "enum",
      tableName: "report",
      columnName: "env",
      sortable: false,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (params: params) => <TotalRenderCell {...params?.row} />,
      customRenderCell: TotalRenderCell,
      type: "number",
      sortable: false,
    },
    {
      field: "success",
      headerName: "Success",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (params: params) => <SuccessRenderCell {...params?.row} />,
      customRenderCell: TotalRenderCell,
      type: "number",
      sortable: false,
    },
    {
      field: "failed",
      headerName: "Failed",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (params: params) => <FailedRenderCell {...params?.row} />,
      customRenderCell: FailedRenderCell,
      type: "number",
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      type: "enum",
      tableName: "report",
      columnName: "status",
      sortable: false,
    },
  ];

  return (
    <Fragment>
      <Table
        columns={columns}
        dataPosition={"reports"}
        endpoint={"/report/reports"}
        rowId={"id"}
        checkBoxSelection={false}
        tabName={"reportService"}
        service="reportService"
        setData={setReportsData}
        data={reportsData}
        navigateOnRowClickEndpoint={`/reportServiceNew`}
        mobileResponsive={true}
        columnVisibilityModel={columnVisibilityModel}
        clickable={true}
      />
    </Fragment>
  );
};

export default ReportServiceNew;
